/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React from 'react';
import { removeHoldingFromPortfolio } from '../../actions/portfolio-tracker';
import { DataShareScopeView, DataShareStatus } from '../../types/dataShare';
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown, UncontrolledDropdownProps } from 'reactstrap';
import { InitiativeCompany, InitiativeType } from '../../types/initiative';
import SimpleTooltip from '../simple-tooltip';
import { isNoAccess, isViewShared } from '../../utils/dataShare';

export const NO_ACCESS_MESSAGE = 'Company is not open for data sharing or messaging. Unable to process requests.';

type OnShareFn = (company: Pick<InitiativeCompany, '_id' | 'name'>) => void;
type SendMessageFn = (company: Pick<InitiativeCompany, '_id' | 'name'>) => void

interface DataShareButtonProps {
  portfolioId: string;
  holdingId: string;
  company?: Pick<InitiativeCompany, '_id' | 'name' | 'type' | 'dataShare' | 'requestedDataShares'>,
  onShare: OnShareFn
  disabled?: boolean;
}

interface MenuButtonProps extends DataShareButtonProps {
  isPortfolio?: boolean;
  holdingId: string;
  handleReload: () => void;
  setSaving: (saving: boolean) => void;
  dropdownProps?: UncontrolledDropdownProps;
  handleSendMessage: SendMessageFn;
}

const DataShareButton = ({ company, onShare, disabled }: DataShareButtonProps) => {

  if (!company || company.type !== InitiativeType.initiative) {
    return null;
  }

  const dataShare = company.requestedDataShares;
  const hasData = dataShare && dataShare.length > 0

  if (!hasData) {
    return <>
      <DropdownItem size='sm' disabled={disabled || hasData} onClick={() => onShare(company)}>
        <i className='fa fa-bell mr-2' />Request access
      </DropdownItem>
      <DropdownItem divider />
    </>;
  }

  const hasAccepted = dataShare.some(s => s.status === DataShareStatus.Accepted)

  if (hasAccepted) {
    return <>
      <DropdownItem size='sm' disabled>
        <i className='fa fa-check text-ThemeSuccessMedium mr-2' />Access granted
      </DropdownItem>
      <DropdownItem divider />
    </>;
  }

  return <>
    <DropdownItem size='sm' disabled>
      <i className='fa fa-bell mr-2' />Data share request is pending
    </DropdownItem>
    <DropdownItem divider />
  </>;
};

export const MenuButton = (props: MenuButtonProps) => {

  const {
    portfolioId,
    holdingId,
    disabled,
    setSaving,
    handleReload,
    company,
    onShare,
    isPortfolio,
    dropdownProps,
    handleSendMessage,
  } = props;

  const handleRemove = async (holdingId: string) => {
    setSaving(true);
    try {
      await removeHoldingFromPortfolio(portfolioId, holdingId);
      await handleReload();
    } catch (e) {
      console.log(e);
    }
    setSaving(false);
  }

  const CompanyDropdownItem = (props: any) => isPortfolio ? null : <DropdownItem {...props} />;

  if (!company) {
    return null;
  }

  return (
    <UncontrolledDropdown {...dropdownProps}>
      <DropdownToggle outline={true} color='secondary' className='px-2'>
        <i className='fas fa-bars' />
      </DropdownToggle>
      <DropdownMenu>
        <CompanyDropdownItem size='sm' disabled={disabled || !isViewShared(company, DataShareScopeView.Messaging)} onClick={() => handleSendMessage(company)}>
          <i className='fa fa-envelope mr-2' />Send a message
        </CompanyDropdownItem>
        <CompanyDropdownItem divider={true} />
        <SimpleTooltip text={isNoAccess(company) ? NO_ACCESS_MESSAGE : undefined}>
          <DataShareButton portfolioId={portfolioId} holdingId={holdingId} company={company} onShare={onShare} disabled={disabled || isNoAccess(company)} />
        </SimpleTooltip>
        <DropdownItem disabled={disabled} size='sm' onClick={() => handleRemove(holdingId)}>
          <i className='fa fa-times mr-2 text-ThemeDangerMedium' />Remove from portfolio
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  )
}
